/**
 * A modal panel over a dimmed screen (#82): centred left to right, with its top edge placed
 * once, when it opens, 15% of the way down the viewport.
 *
 * That is deliberately high. The first version put the *top* at 40% of the free height, which
 * read as the lower half of the screen; what was wanted was the form sitting in the upper part
 * with the room below it kept clear. The top is fixed there and **not recomputed** as the
 * content changes, so a note's text area growing as it is typed in pushes the bottom edge down
 * into that room instead of the top edge sliding up the screen. If the content outgrows what is
 * left, the panel scrolls inside itself.
 *
 * A native `<dialog>` opened with `showModal()`: it brings the focus trap, the inert page
 * behind it and Esc-to-close, and renders in the top layer so no card's stacking context can
 * put it underneath anything. Clicking the dimmed area closes it, like Esc does.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";

/** How far down the viewport the panel's top edge sits when it opens. */
const TOP_FRACTION = 0.15;
const MARGIN_PX = 16;

export function Modal({
  label,
  onClose,
  children,
}: {
  /** The panel's accessible name. */
  label: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [top, setTop] = useState<number | null>(null);
  const pressedOnBackdrop = useRef(false);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    // jsdom has no `showModal`; fall back to the plain `open` attribute so tests still render it.
    if (typeof dialog.showModal === "function") dialog.showModal();
    else dialog.setAttribute("open", "");
    setTop(Math.max(MARGIN_PX, Math.round(window.innerHeight * TOP_FRACTION)));
    return () => {
      if (typeof dialog.close === "function" && dialog.open) dialog.close();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      className="modal"
      aria-label={label}
      // `--modal-top` lets the stylesheet work out how tall the panel may get from where it landed.
      style={
        top === null
          ? { visibility: "hidden" }
          : ({ top, "--modal-top": `${top}px` } as React.CSSProperties)
      }
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onPointerDown={(event) => {
        pressedOnBackdrop.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        // A click on the backdrop lands on the dialog element itself, outside its box. Both
        // tests matter: choosing from a dropdown inside the form sends a click from the
        // <select> whose position is in the open list, which can be outside the box (or 0,0),
        // and closed the popup mid-choice; and a press that started inside (a text selection
        // dragged out) is not a click on the backdrop either.
        if (event.target !== event.currentTarget || !pressedOnBackdrop.current) return;
        const box = event.currentTarget.getBoundingClientRect();
        const outside =
          event.clientX < box.left ||
          event.clientX > box.right ||
          event.clientY < box.top ||
          event.clientY > box.bottom;
        if (outside) onClose();
      }}
    >
      {children}
    </dialog>
  );
}
